import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import { ContentActionsMenu, EditedLabel } from "../components/ContentActionsMenu";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  MapPin, Plus, Calendar, Clock, Users,
  Trash2, Tag, MessageCircle, Send
} from "lucide-react";

const PROVINCES = [
  { code: "AB", name: "Alberta" }, { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" }, { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland & Labrador" }, { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" }, { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" }, { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" }, { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

const MeetupsPage = () => {
  const { user } = useAuth();
  const [meetups, setMeetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provinceFilter, setProvinceFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeMeetup, setActiveMeetup] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const emptyForm = () => ({
    title: "", description: "", city: "", province: user?.province || "",
    address: "", event_date: "", event_time: "", max_attendees: "", tags: ""
  });
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const setFormDialog = (open) => {
    setShowCreate(open);
    if (!open) { setEditingId(null); setForm(emptyForm()); }
  };

  const startEdit = (m) => {
    setEditingId(m.meetup_id);
    setForm({
      title: m.title || "", description: m.description || "", city: m.city || "", province: m.province || "",
      address: m.address || "", event_date: m.event_date || "", event_time: m.event_time || "",
      max_attendees: m.max_attendees ? String(m.max_attendees) : "", tags: (m.tags || []).join(", "),
    });
    setActiveMeetup(null);
    setShowCreate(true);
  };

  const fetchMeetups = useCallback(async () => {
    setLoading(true);
    try {
      const params = provinceFilter ? `?province=${provinceFilter}` : "";
      const res = await axios.get(`${API}/meetups${params}`);
      setMeetups(res.data);
    } catch { toast.error("Failed to load meetups"); }
    finally { setLoading(false); }
  }, [provinceFilter]);

  useEffect(() => { fetchMeetups(); }, [fetchMeetups]);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.city.trim() || !form.province || !form.event_date) {
      toast.error("Title, city, province, and date are required"); return;
    }
    setCreating(true);
    try {
      const tags = form.tags.split(",").map(t => t.trim()).filter(Boolean);
      const payload = {
        ...form,
        max_attendees: form.max_attendees ? parseInt(form.max_attendees) : null,
        tags,
      };
      if (editingId) {
        const res = await axios.put(`${API}/meetups/${editingId}`, payload);
        toast.success("Meetup updated");
        setMeetups(prev => prev.map(m => m.meetup_id === editingId ? res.data : m).sort((a, b) => a.event_date.localeCompare(b.event_date)));
        setFormDialog(false);
        return;
      }
      const res = await axios.post(`${API}/meetups`, payload);
      toast.success("Meetup created!");
      setMeetups(prev => [...prev, res.data].sort((a, b) => a.event_date.localeCompare(b.event_date)));
      setShowCreate(false);
      setForm({ title: "", description: "", city: "", province: user?.province || "", address: "", event_date: "", event_time: "", max_attendees: "", tags: "" });
      setActiveMeetup(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingId ? "Failed to update meetup" : "Failed to create meetup"));
    } finally { setCreating(false); }
  };

  const handleRSVP = async (meetupId) => {
    try {
      const res = await axios.post(`${API}/meetups/${meetupId}/rsvp`);
      setMeetups(prev => prev.map(m => m.meetup_id === meetupId ? {
        ...m,
        is_attending: res.data.attending,
        attendee_count: m.attendee_count + (res.data.attending ? 1 : -1),
        attendees: res.data.attending ? [...m.attendees, user.user_id] : m.attendees.filter(a => a !== user.user_id),
      } : m));
      if (activeMeetup?.meetup_id === meetupId) {
        setActiveMeetup(prev => ({ ...prev, is_attending: res.data.attending, attendee_count: prev.attendee_count + (res.data.attending ? 1 : -1) }));
      }
      toast.success(res.data.attending ? "You're in!" : "RSVP cancelled");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (meetupId) => {
    try {
      await axios.delete(`${API}/meetups/${meetupId}`);
      toast.success("Meetup deleted");
      setMeetups(prev => prev.filter(m => m.meetup_id !== meetupId));
      if (activeMeetup?.meetup_id === meetupId) setActiveMeetup(null);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const formatDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  // ----- Comments -----
  useEffect(() => {
    if (!activeMeetup) { setComments([]); setCommentInput(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/meetups/${activeMeetup.meetup_id}/comments`);
        if (!cancelled) setComments(res.data || []);
      } catch (err) {
        console.error("[MeetupsPage] fetch comments failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeMeetup?.meetup_id]);

  const handlePostComment = async () => {
    if (!activeMeetup || !commentInput.trim() || postingComment) return;
    setPostingComment(true);
    try {
      const res = await axios.post(`${API}/meetups/${activeMeetup.meetup_id}/comments`, { content: commentInput.trim() });
      setComments(prev => [...prev, res.data]);
      setCommentInput("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not post comment");
    } finally { setPostingComment(false); }
  };

  const handleDeleteComment = async (commentId) => {
    if (!activeMeetup) return;
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(`${API}/meetups/${activeMeetup.meetup_id}/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="meetups-page">
        {/* Hero */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#6B4E8A] flex items-center justify-center"><MapPin className="w-5 h-5 text-[#8DB5A0]" /></div>
              <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <EditableText blockKey="meetups.title" fallback="Local Meetups" as="span" />
              </h1>
            </div>
            <EditableText
              blockKey="meetups.subtitle"
              fallback="In-person hangouts, study sessions, and CE workshops near you. Browse upcoming meetups and RSVP to connect IRL."
              as="p"
              className="text-[#64748B] max-w-xl"
              multiline
            />
          </div>
          <div className="flex items-center gap-2">
            <Select value={provinceFilter || "all"} onValueChange={v => setProvinceFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-10 w-[180px] bg-white border-[#E5DFEC]" data-testid="province-filter"><SelectValue placeholder="All provinces" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All provinces</SelectItem>
                {PROVINCES.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => setShowCreate(true)} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full" data-testid="create-meetup-btn">
              <Plus className="w-4 h-4 mr-2" />Host Meetup
            </Button>
          </div>
        </div>

        <div className="space-y-3" data-testid="meetups-list">
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}</div>
          ) : meetups.length > 0 ? (
            meetups.map((m, i) => (
              <MeetupRow key={m.meetup_id} meetup={m} index={i} onView={() => setActiveMeetup(m)} onRSVP={() => handleRSVP(m.meetup_id)} onEdit={() => startEdit(m)} onDelete={() => handleDelete(m.meetup_id)} />
            ))
          ) : (
            <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
              <MapPin className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
              <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No meetups yet</h3>
              <p className="text-[#64748B] mb-4">Host the first one — coffee, study sessions, or CE workshops welcome!</p>
              <Button onClick={() => setShowCreate(true)} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full"><Plus className="w-4 h-4 mr-2" />Host Meetup</Button>
            </div>
          )}
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setFormDialog}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="create-meetup-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editingId ? "Edit Meetup" : "Host a Meetup"}</DialogTitle>
              <DialogDescription>Coffee, walks, study sessions, CE workshops — keep it real and local.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} maxLength={100} placeholder="Saturday morning coffee for AB hygienists" className="mt-1.5" data-testid="meetup-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>City *</Label>
                  <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Calgary" className="mt-1.5" data-testid="meetup-city" />
                </div>
                <div>
                  <Label>Province *</Label>
                  <Select value={form.province || ""} onValueChange={v => setForm(f => ({ ...f, province: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="meetup-province"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{PROVINCES.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Address (optional)</Label>
                <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Main St" className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))} className="mt-1.5" data-testid="meetup-date" />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input type="time" value={form.event_time} onChange={e => setForm(f => ({ ...f, event_time: e.target.value }))} className="mt-1.5" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Max attendees</Label>
                  <Input type="number" min="1" value={form.max_attendees} onChange={e => setForm(f => ({ ...f, max_attendees: e.target.value }))} placeholder="Unlimited" className="mt-1.5" />
                </div>
                <div>
                  <Label>Tags (comma-separated)</Label>
                  <Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="coffee, social" className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} maxLength={1500} placeholder="What's the plan? Any prep needed?" className="mt-1.5 min-h-[80px]" data-testid="meetup-description" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setFormDialog(false)} className="rounded-full">Cancel</Button>
                <Button onClick={handleCreate} disabled={creating} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full" data-testid="submit-meetup">
                  {creating ? "Saving..." : editingId ? "Save changes" : "Create Meetup"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Detail dialog */}
        <Dialog open={!!activeMeetup} onOpenChange={v => !v && setActiveMeetup(null)}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="meetup-detail-dialog">
            {activeMeetup && (
              <>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{activeMeetup.title}</DialogTitle>
                  <DialogDescription>Hosted by {activeMeetup.host_name} <EditedLabel editedAt={activeMeetup.edited_at} testId="meetup-detail-edited" /></DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  <div className="flex flex-wrap gap-3 text-sm text-[#64748B]">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(activeMeetup.event_date)}</span>
                    {activeMeetup.event_time && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{activeMeetup.event_time}</span>}
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{activeMeetup.city}, {activeMeetup.province}</span>
                    <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{activeMeetup.attendee_count}{activeMeetup.max_attendees ? ` / ${activeMeetup.max_attendees}` : ""} going</span>
                  </div>
                  {activeMeetup.address && <p className="text-sm text-[#475569] bg-[#FAF7F2] rounded-xl p-3">{activeMeetup.address}</p>}
                  {activeMeetup.description && <p className="text-sm text-[#6B4E8A] whitespace-pre-wrap">{activeMeetup.description}</p>}
                  {activeMeetup.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {activeMeetup.tags.map(t => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F0ECF7] text-[#64748B] inline-flex items-center gap-1"><Tag className="w-3 h-3" />{t}</span>)}
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button onClick={() => handleRSVP(activeMeetup.meetup_id)} className={`rounded-full flex-1 ${activeMeetup.is_attending ? "bg-[#10B981] text-white hover:bg-[#059669]" : "bg-[#6B4E8A] text-white hover:bg-[#553D6F]"}`} data-testid="detail-rsvp-btn">
                      {activeMeetup.is_attending ? "Going ✓" : "RSVP"}
                    </Button>
                    <ContentActionsMenu
                        canManage={activeMeetup.can_manage}
                        reportType="meetup"
                        reportId={activeMeetup.meetup_id}
                        testIdPrefix="meetup-detail"
                        onEdit={() => startEdit(activeMeetup)}
                        onDelete={() => handleDelete(activeMeetup.meetup_id)}
                        deleteTitle="Delete this meetup?"
                        deleteDescription="This permanently removes the meetup, its RSVPs and all comments. This cannot be undone."
                      />
                  </div>

                  {/* Comments */}
                  <div className="pt-4 border-t border-[#E5DFEC]" data-testid="meetup-comments-section">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageCircle className="w-4 h-4 text-[#8DB5A0]" />
                      <h4 className="text-sm font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Comments {comments.length > 0 && <span className="text-[#94A3B8] font-normal">({comments.length})</span>}
                      </h4>
                    </div>
                    {comments.length === 0 ? (
                      <p className="text-xs text-[#94A3B8] mb-3">Be the first to say hello to the host.</p>
                    ) : (
                      <div className="space-y-2 mb-3 max-h-56 overflow-y-auto pr-1" data-testid="meetup-comments-list">
                        {comments.map(c => {
                          const isMine = c.author_id === user?.user_id;
                          const isHost = c.author_id === activeMeetup.host_id;
                          const canDelete = isMine || activeMeetup.is_host || user?.is_admin;
                          return (
                            <div key={c.comment_id} className="bg-[#FAF7F2] rounded-xl p-3" data-testid={`comment-${c.comment_id}`}>
                              <div className="flex items-center gap-2 mb-1">
                                {c.author_picture ? (
                                  <img src={c.author_picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                                ) : (
                                  <div className="w-6 h-6 rounded-full bg-[#8DB5A0] text-white text-[10px] flex items-center justify-center">{(c.author_name || "M")[0]}</div>
                                )}
                                <span className="text-xs font-medium text-[#6B4E8A]">{c.author_name}</span>
                                {isHost && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#8DB5A0]/20 text-[#558471]">Host</span>}
                                <span className="text-[10px] text-[#94A3B8] ml-auto">{timeAgo(c.created_at)}</span>
                                {canDelete && (
                                  <button onClick={() => handleDeleteComment(c.comment_id)} className="p-1 rounded hover:bg-[#EF4444]/10" data-testid={`delete-comment-${c.comment_id}`} title="Delete">
                                    <Trash2 className="w-3 h-3 text-[#EF4444]" />
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-[#475569] whitespace-pre-wrap break-words">{c.content}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-end gap-2">
                      <Textarea
                        value={commentInput}
                        onChange={(e) => setCommentInput(e.target.value)}
                        maxLength={800}
                        placeholder="Add a comment…"
                        rows={2}
                        className="text-sm min-h-[44px] flex-1 bg-white border-[#E5DFEC]"
                        data-testid="meetup-comment-input"
                      />
                      <Button
                        size="sm"
                        onClick={handlePostComment}
                        disabled={!commentInput.trim() || postingComment}
                        className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] h-9 px-3 shrink-0"
                        data-testid="post-meetup-comment-btn"
                      >
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const MeetupRow = ({ meetup, index, onView, onRSVP, onEdit, onDelete }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}
    className="bg-white border border-[#E5DFEC] rounded-2xl p-4 flex items-start gap-4"
    data-testid={`meetup-row-${meetup.meetup_id}`}
  >
    <div className="w-14 h-14 rounded-xl bg-[#8DB5A0] text-white flex flex-col items-center justify-center shrink-0">
      <span className="text-[10px] font-medium opacity-80">{new Date(meetup.event_date + "T00:00").toLocaleDateString("en-CA", { month: "short" }).toUpperCase()}</span>
      <span className="text-lg font-bold leading-none">{new Date(meetup.event_date + "T00:00").getDate()}</span>
    </div>
    <div className="flex-1 min-w-0">
      <button onClick={onView} className="text-left">
        <h3 className="font-medium text-[#6B4E8A] truncate hover:text-[#8DB5A0] transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>{meetup.title}</h3>
      </button>
      <div className="flex flex-wrap gap-2 text-xs text-[#64748B] mt-1">
        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{meetup.city}, {meetup.province}</span>
        {meetup.event_time && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{meetup.event_time}</span>}
        <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{meetup.attendee_count}{meetup.max_attendees ? ` / ${meetup.max_attendees}` : ""} going</span>
        <span className="text-[#94A3B8]">· hosted by {meetup.host_name}</span>
        <EditedLabel editedAt={meetup.edited_at} testId={`meetup-${meetup.meetup_id}-edited`} />
      </div>
      {meetup.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">{meetup.tags.slice(0, 4).map(t => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F0ECF7] text-[#64748B]">{t}</span>)}</div>
      )}
    </div>
    <div className="flex items-start gap-1 shrink-0">
      <Button onClick={onRSVP} size="sm" className={`rounded-full text-xs h-8 ${meetup.is_attending ? "bg-[#10B981] text-white hover:bg-[#059669]" : "bg-[#F0ECF7] text-[#6B4E8A] hover:bg-[#E5DFEC]"}`} data-testid={`rsvp-${meetup.meetup_id}`}>
        {meetup.is_attending ? "Going" : "RSVP"}
      </Button>
      <ContentActionsMenu
          canManage={meetup.can_manage}
          reportType="meetup"
          reportId={meetup.meetup_id}
          testIdPrefix={`meetup-${meetup.meetup_id}`}
          onEdit={onEdit}
          onDelete={onDelete}
          deleteTitle="Delete this meetup?"
          deleteDescription="This permanently removes the meetup, its RSVPs and all comments. This cannot be undone."
        />
    </div>
  </motion.div>
);

export default MeetupsPage;
